import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ChatLayout } from '@/components/ChatLayout'
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog'
import { AuthConfig, HealthResponse } from '@/lib/types'
import { authService } from '@/lib/auth'
import { apiClient, TokenExpiredError } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

interface ChatPageProps {
  authConfig: AuthConfig
  healthStatus: HealthResponse
}

export function ChatPage({ authConfig, healthStatus }: ChatPageProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showSessionExpired, setShowSessionExpired] = useState(false)
  const { toast } = useToast()
  const navigate = useNavigate()

  const initializeChat = useCallback(async () => {
    try {
      setIsLoading(true)
      
      // Authentication is always required
      const token = await authService.getToken()
      if (!token) {
        throw new Error('Authentication required')
      }
      apiClient.setAuthToken(token)

      // Test API connection
      await apiClient.getHealth()
      
    } catch (error) {
      if (error instanceof TokenExpiredError) {
        setShowSessionExpired(true)
        return
      }
      
      // Handle other errors
      console.error('Failed to initialize chat:', error)
      
      // Handle token expiration - show session expired dialog
      if (error instanceof TokenExpiredError) {
        setShowSessionExpired(true)
        return
      }
      
      // Handle other errors
      setError(error instanceof Error ? error.message : 'Failed to initialize chat')
      toast({
        title: "Connection Error",
        description: "Failed to connect to the chat service. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast, navigate])

  useEffect(() => {
    initializeChat()
  }, [initializeChat])

  const handleLogout = async () => {
    try {
      await authService.logout()
      window.location.reload()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="spinner w-8 h-8"></div>
          <p className="text-muted-foreground">Initializing chat...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <h1 className="text-2xl font-bold text-destructive">Connection Failed</h1>
          <p className="text-muted-foreground">{error}</p>
          <div className="space-x-2">
            <Button onClick={initializeChat}>
              Retry
            </Button>
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <ChatLayout 
        authConfig={authConfig}
        healthStatus={healthStatus}
        onLogout={handleLogout}
      />
      
      {/* Session Expired Dialog */}
      {showSessionExpired && (
        <SessionExpiredDialog
          onNavigate={() => setShowSessionExpired(false)}
        />
      )}
    </div>
  )
}

